import {
  ApiResponseSettings,
  Setting,
  CategorySetting,
  RawTermSettingValue,
} from 'types/settings';
import { apiClient } from './index';

// Define request types locally with different names to avoid conflicts
interface SettingAddRequest<
  T extends Setting['setting_type'],
  C extends string
> {
  action: 'add_setting';
  setting_key: string;
  setting_value: string;
  setting_type: T;
  category: C;
  description?: string | null;
}

interface SettingUpdateRequest<
  T extends Setting['setting_type'],
  C extends string
> {
  action: 'edit_setting';
  setting_id: number;
  setting_key: string;
  setting_value: string;
  setting_type: T;
  category: C;
  description?: string | null;
}

// Base API functions
export const getSettings = async (params?: {
  keyword?: string;
  category?: string;
  status?: string;
}): Promise<ApiResponseSettings<Setting[]>> => {
  const response = await apiClient.get<ApiResponseSettings<Setting[]>>(
    '/admin/settings',
    {
      params,
    }
  );
  return response.data;
};

export const addSetting = async <
  T extends Setting['setting_type'],
  C extends string
>(
  data: SettingAddRequest<T, C>
): Promise<ApiResponseSettings<{ setting_id: number }>> => {
  const response = await apiClient.post<
    ApiResponseSettings<{ setting_id: number }>
  >('/admin/settings', data);
  return response.data;
};

export const updateSetting = async <
  T extends Setting['setting_type'],
  C extends string
>(
  data: SettingUpdateRequest<T, C>
): Promise<ApiResponseSettings<void>> => {
  const response = await apiClient.put<ApiResponseSettings<void>>(
    '/admin/settings',
    data
  );
  return response.data;
};

export const deleteSetting = async (
  settingId: number
): Promise<ApiResponseSettings<void>> => {
  const response = await apiClient.delete<ApiResponseSettings<void>>(
    '/admin/settings',
    {
      params: { id: settingId },
      data: {
        action: 'delete_setting',
        setting_id: settingId,
      },
    }
  );
  return response.data;
};

// Generic factory function
export function createSettingsAPI<
  V,
  C extends string,
  T extends Setting['setting_type']
>(category: C, settingType: T) {
  return {
    getSettings: async (
      keyword?: string
    ): Promise<ApiResponseSettings<CategorySetting<V, C>[]>> => {
      const response = await getSettings({ category, keyword });
      return {
        ...response,
        data: response.data.map((setting) => ({
          ...setting,
          category: category as C,
          setting_value: {
            value:
              typeof setting.setting_value.value === 'string'
                ? JSON.parse(setting.setting_value.value)
                : setting.setting_value.value,
          },
        })),
      };
    },

    addSetting: async (
      setting_key: string,
      setting_value: V,
      options?: { description?: string | null }
    ): Promise<ApiResponseSettings<{ setting_id: number }>> => {
      const data: SettingAddRequest<T, C> = {
        action: 'add_setting',
        setting_key,
        setting_value: JSON.stringify(setting_value),
        setting_type: settingType,
        category,
        description: options?.description,
      };
      return addSetting(data);
    },

    updateSetting: async (
      setting_id: number,
      setting_value: V,
      setting_key: string,
      options?: { description?: string | null }
    ): Promise<ApiResponseSettings<void>> => {
      const data: SettingUpdateRequest<T, C> = {
        action: 'edit_setting',
        setting_id,
        setting_key,
        setting_value: JSON.stringify(setting_value),
        setting_type: settingType,
        category,
        description: options?.description,
      };
      return updateSetting(data);
    },

    deleteSetting,
  };
}

// Create specialized APIs
export const termsAPI = createSettingsAPI<RawTermSettingValue, 'terms', 'html'>(
  'terms',
  'html'
);

/*
export const jsonAPI = createSettingsAPI<JsonSettingValue, 'json-settings', 'json'>(
  'json-settings',
  'json'
);
*/
